/**
 * Questo file definisce il componente React per la pagina di Login.
 * - Fornisce un form per l'inserimento di email e password.
 * - Gestisce la validazione dei dati del form a livello di frontend.
 * - Mostra un messaggio di successo se l'utente arriva dalla pagina di registrazione.
 * - Fa il dispatch dell'azione `loginUser` di Redux per autenticare l'utente.
 * - In caso di successo, reindirizza l'utente alla dashboard o alla pagina
 * precedentemente richiesta.
 * - Mostra eventuali errori di autenticazione restituiti dall'API.
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../redux/authSlice';
import Navbar from '../components/Navbar';
import './Auth.css';

/**
 * COMPONENTE PRINCIPALE: Login
 */
export default function Login() {
    // Stato per i dati del form (email, password).
    const [formData, setFormData] = useState({ email: '', password: '' });
    // Stato per gli errori di validazione del form.
    const [formErrors, setFormErrors] = useState({});
    // Stato per mostrare il messaggio di successo post-registrazione.
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    // `useLocation` per ottenere informazioni sulla rotta corrente, utile per il reindirizzamento.
    const location = useLocation();
    // `useSearchParams` per leggere i parametri dall'URL (es. ?registration=success).
    const [searchParams] = useSearchParams();
    // Estrae `loading` e `error` dallo stato di autenticazione di Redux.
    const { loading, error: apiError } = useSelector((state) => state.auth);

    /**
     * EFFETTO: Controlla la presenza del parametro 'registration' nell'URL.
     * - `useEffect` viene eseguito al montaggio del componente.
     * - Se rileva `registration=success`, imposta lo stato per mostrare il messaggio di benvenuto.
     */
    useEffect(() => {
        if (searchParams.get('registration') === 'success') {
            setRegistrationSuccess(true);
        }
    }, [searchParams]);
    
    // Gestore per aggiornare lo stato `formData` quando l'utente digita nei campi.
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    /**
     * FUNZIONE: Validazione del form.
     * - Controlla che i campi email e password non siano vuoti.
     * - Popola lo stato `formErrors` con eventuali messaggi di errore.
     * - Ritorna `true` se il form è valido, altrimenti `false`.
     */
    const validateForm = () => {
        const errors = {};
        if (!formData.email) errors.email = "L'email è obbligatoria.";
        if (!formData.password) errors.password = "La password è obbligatoria.";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /**
     * GESTORE EVENTO: Invio del form.
     * - Impedisce il comportamento predefinito del form.
     * - Esegue la validazione; se fallisce, interrompe l'esecuzione.
     * - Esegue il dispatch asincrono dell'azione `loginUser`.
     * - Utilizza `try...catch` per gestire il flusso asincrono:
     * - Se l'azione `loginUser` ha successo (`fulfilled`), reindirizza l'utente.
     * - Il `location.state?.from?.pathname` è un trucco per reindirizzare l'utente alla
     * pagina che stava cercando di visitare prima di essere mandato al login (se esiste).
     * - Se l'azione fallisce (`rejected`), l'errore API verrà automaticamente
     * mostrato tramite lo stato `apiError` di Redux.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const resultAction = await dispatch(loginUser(formData));
            
            if (loginUser.fulfilled.match(resultAction)) {
                const from = location.state?.from?.pathname || '/dashboard';
                navigate(from, { replace: true });
            }
        } catch (err) {
            console.error('Failed to login:', err);
        }
    };

    return (
        <>
            <Navbar />
            <div className="auth-page-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-8 col-lg-6">
                            <div className="auth-card p-4 p-sm-5">
                                <h2 className="text-center fw-bold mb-4">Bentornato!</h2>
                                {/* Messaggio di successo post-registrazione */}
                                {registrationSuccess && (
                                    <div className="alert alert-success">
                                        Registrazione completata! Ora puoi accedere.
                                    </div>
                                )}
                                {/* Messaggio di errore dall'API */}
                                {apiError && <div className="alert alert-danger">{apiError}</div>}
                                
                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="mb-3">
                                        <label htmlFor="email" className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                        {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label">Password</label>
                                        <input
                                            type="password"
                                            className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                                    </div>
                                    <div className="d-flex justify-content-end mb-4">
                                        <Link to="#">Password dimenticata?</Link>
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                                            {loading ? 'Accesso in corso...' : 'Accedi'}
                                        </button>
                                    </div>
                                </form>
                                <p className="text-center mt-4 mb-0">
                                    Non hai un account? <Link to="/register">Registrati ora</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}